import { MarketStatus, TradePosition } from '@sportspredict/shared';

export const getNormalizedLobbyUserId = (value: any) => {
	if (value === null || value === undefined) return '';
	if (typeof value === 'string' || typeof value === 'number') return String(value);

	return String(value?.userId ?? value?.user_id ?? value?.id ?? value?.user?.id ?? '');
};

export const getUserLobbyEliminationRecord = (lobby: any, userId: any, lobbyId: any) => {
	if (!userId) return null;

	const eliminatedSource = lobby?.eliminated_users ?? lobby?.elminated_users ?? [];
	const eliminatedList = Array.isArray(eliminatedSource) ? eliminatedSource : [];
	const normalizedUserId = String(userId);
	const normalizedLobbyId = lobbyId != null ? String(lobbyId) : null;

	const matchingRecords = eliminatedList.filter((entry: any) => {
		if (getNormalizedLobbyUserId(entry) !== normalizedUserId) return false;

		if (!normalizedLobbyId) return true;

		const entryLobbyId = entry?.lobbyId ?? entry?.lobby_id;
		if (entryLobbyId == null) return true;

		return String(entryLobbyId) === normalizedLobbyId;
	});

	if (!matchingRecords.length) return null;

	return [...matchingRecords].sort(
		(a: any, b: any) => Number(b?.round ?? -1) - Number(a?.round ?? -1)
	)[0];
};

export const getActiveEliminatedUserIds = (lobby: any) => {
	const eliminatedSource = lobby?.eliminated_users ?? lobby?.elminated_users ?? [];
	const eliminatedList = Array.isArray(eliminatedSource) ? eliminatedSource : [];

	return Array.from(new Set(eliminatedList.map(getNormalizedLobbyUserId).filter(Boolean)));
};

export const sortOpenMarketsByPriorityStable = (items: any[]) =>
	[...(items || [])].sort((a: any, b: any) => {
		const isSecondChanceA = a?.market?.is_second_chance;
		const isSecondChanceB = b?.market?.is_second_chance;

		const pa = isSecondChanceA ? a?.market?.second_chance_priority : (a?.market?.priority ?? null);
		const pb = isSecondChanceB ? b?.market?.second_chance_priority : (b?.market?.priority ?? null);

		if (pa != null && pb != null) return pa - pb;
		if (pa != null) return -1;
		if (pb != null) return 1;

		const ca = a?.market?.created_date ? new Date(a.market.created_date).getTime() : 0;
		const cb = b?.market?.created_date ? new Date(b.market.created_date).getTime() : 0;
		return ca - cb;
	});

export const byPriorityAsc = (a: any, b: any) => {
	const pa = a?.is_second_chance ? a?.second_chance_priority : a?.priority;
	const pb = b?.is_second_chance ? b?.second_chance_priority : b?.priority;
	return Number(pa ?? 0) - Number(pb ?? 0);
};
export const byPriorityDesc = (a: any, b: any) => {
	const pa = a?.is_second_chance ? a?.second_chance_priority : a?.priority;
	const pb = b?.is_second_chance ? b?.second_chance_priority : b?.priority;
	return Number(pb ?? 0) - Number(pa ?? 0);
};
export const pickCrowdLockCurrentMarket = (
	markets: any[] = [],
	options: { isSecondChance?: boolean; isAdmin?: boolean } = {
		isSecondChance: false,
		isAdmin: false,
	}
) => {
	const { isSecondChance } = options;
	if (isSecondChance) {
		const secondChance = markets.filter((m) => m.is_second_chance);

		if (secondChance.length) {
			return [...secondChance].sort(byPriorityAsc)[0];
		}

		return null;
	}

	const nonSecondChanceMarkets = markets.filter((m) => !m.is_second_chance);

	const live = nonSecondChanceMarkets.filter((m) => m.status === MarketStatus.Live);
	if (live.length) return [...live].sort(byPriorityDesc)[0];
	const progressed = nonSecondChanceMarkets.filter(
		(m) =>
			m.status === MarketStatus.Closed ||
			m.status === MarketStatus.Settled ||
			m.status === MarketStatus.Void
	);
	if (progressed.length) return [...progressed].sort(byPriorityDesc)[0];

	const openStatuses = options.isAdmin
		? [MarketStatus.Open, MarketStatus.Queue]
		: [MarketStatus.Open];

	const open = nonSecondChanceMarkets.filter((m) => openStatuses.includes(m.status));
	if (open.length) return [...open].sort(byPriorityAsc)[0];
	return null;
};
export const mapMarketCategoryName = (name: string) => {
	const mapping: Record<string, string> = {
		Moneyline: 'Match Outcome',
		'Player vs Player Props': 'Head-to-Head',
	};

	return mapping[name] || name;
};

export const directionToPosition = (dir: string) => {
	if (dir === 'right') return TradePosition.Long;
	if (dir === 'left') return TradePosition.Short;
	return null;
};

export const buildLobbyMarketGroups = (
	markets: any[],
	userTrades: any[],
	tradesByMarket: Record<string, any>,
	lobbyId: any,
	isSurvivorMode: boolean,
	isCrowdLockMode: boolean
) => {
	const result: {
		openForPredictions: any[];
		liveTradeTracking: any[];
		closedForTrading: any[];
		settled: any[];
		recentlyTraded: any[];
	} = {
		openForPredictions: [],
		liveTradeTracking: [],
		closedForTrading: [],
		settled: [],
		recentlyTraded: [],
	};

	markets.forEach((market) => {
		const trade = userTrades.find((t) => t.market_id === market.id);
		const trades = tradesByMarket?.[market.id];

		if (!isSurvivorMode && !isCrowdLockMode) {
			if (trade && trade.lobby_id === lobbyId && market.status === MarketStatus.Settled) {
				result.settled.push({ market, trade, trades });
				return;
			}

			if (trade && trade.lobby_id === lobbyId) {
				result.recentlyTraded.push({ market, trade, trades });
				return;
			}

			if ((market.status === MarketStatus.Open || market.status === MarketStatus.Live) && !trade) {
				result.openForPredictions.push({ market, trades });
				return;
			}

			if (
				(market.status === MarketStatus.Closed || market.status === MarketStatus.Settled) &&
				!trade
			) {
				result.closedForTrading.push({ market, trades });
			}
		}

		if (isSurvivorMode) {
			if (trade && trade.lobby_id === lobbyId && market.status === MarketStatus.Settled) {
				const settledTimestamp = new Date(market.settled_date || market.updated_date).getTime();
				const now = Date.now();
				if (now - settledTimestamp < 10000) {
					result.recentlyTraded.push({ market, trade, trades });
				}

				if (market.status === MarketStatus.Settled) {
					result.settled.push({ market, trade, trades });
				}

				return;
			}

			if (trade && trade.lobby_id === lobbyId) {
				result.recentlyTraded.push({ market, trade, trades });
				return;
			}

			if (market.status === MarketStatus.Open && !trade) {
				result.openForPredictions.push({ market, trades });
				return;
			}
		}

		if (isCrowdLockMode) {
			if (market.status === MarketStatus.Settled) {
				result.settled.push({ market, trade, trades });
				return;
			}

			if (
				market.status === MarketStatus.Open ||
				market.status === MarketStatus.SecondChance ||
				market.status === MarketStatus.Live ||
				market.status === (MarketStatus as any).Close ||
				market.status === MarketStatus.Void
			) {
				result.openForPredictions.push({ market, trade, trades });
				return;
			}
		}
	});

	return result;
};
